import {
  BLOCKS,
  type Document as RichTextDocument,
  type TopLevelBlock,
} from '@contentful/rich-text-types';
import { Grid } from '@mui/material';
import GenericComponent, {
  type SectionContent,
} from '@/components/GenericComponent/GenericComponent';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import RichText from '@/components/RichText/RichText';
import useStyles from './BlogRichText.styles';

interface BlogRichTextContent {
  text: RichTextDocument;
}

// The following two functions are here for the purpose of separating the rich text
// document by the embedded content blocks, so that the embedded content can be
// rendered outside of the context of Contentful's rich text renderer. So, instead
// of one rich text document, we have multiple rich text documents interspersed
// with embedded content blocks. We map through these to conditionally render either
// <RichText ... /> or <GenericComponent ... />.
export const makeRichTextDocument = (
  blocks: TopLevelBlock[]
): RichTextDocument => {
  return {
    content: blocks,
    data: {},
    nodeType: BLOCKS.DOCUMENT,
  };
};

export const makeSections = (
  blocks: TopLevelBlock[]
): Array<RichTextDocument | TopLevelBlock> => {
  let textBlocks: TopLevelBlock[] = [];
  const sections: Array<RichTextDocument | TopLevelBlock> = [];

  blocks?.forEach((block: TopLevelBlock) => {
    if (block.nodeType === BLOCKS.EMBEDDED_ENTRY) {
      sections.push(makeRichTextDocument(textBlocks));
      sections.push(block);
      textBlocks = [];
    } else {
      textBlocks.push(block);
    }
  });
  sections.push(makeRichTextDocument(textBlocks));

  return sections;
};

export default function BlogRichText({
  text,
}: BlogRichTextContent): React.JSX.Element {
  const { classes } = useStyles();

  const indexedDocument = makeRichTextDocument(
    text?.content?.map((block: TopLevelBlock, i: number) => ({
      ...block,
      index: i,
    }))
  );

  return (
    <div>
      {makeSections(indexedDocument?.content).map(
        (block: TopLevelBlock | RichTextDocument, i: number) => {
          if (block.nodeType === BLOCKS.DOCUMENT) {
            return (
              <GridContainer
                fixed
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                key={i}
                gridProps={{ className: classes.fixedGrid }}
              >
                <Grid size={{ xs: 12, md: 10 }} offset={{ md: 1 }}>
                  <div className={classes.blogRichText}>
                    <RichText
                      content={block}
                      blockClasses={{
                        p: classes.richTextParagraph,
                        ul: classes.richTextList,
                        ol: classes.richTextList,
                      }}
                    />
                  </div>
                </Grid>
              </GridContainer>
            );
          } else {
            return (
              <Grid
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                key={i}
                className={classes.richTextContent}
              >
                <GenericComponent
                  content={block.data.target as SectionContent}
                />
              </Grid>
            );
          }
        }
      )}
    </div>
  );
}
